import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Tag, Typography, Card, message, Space, Avatar, Popconfirm, Modal, Descriptions } from 'antd';
import { ReloadOutlined, MailOutlined, UserOutlined, ShopOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    
    h2 {
      font-size: 20px !important;
    }
    
    button {
      width: 100%;
    }
  }
`;

const StyledTableCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 0;
  }
  
  @media (max-width: 768px) {
    .ant-card-body {
      padding: 8px !important;
      overflow-x: auto;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    .ant-table {
      min-width: 800px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 4px !important;
      font-size: 12px;
    }
    
    .ant-table-pagination {
      margin: 16px 8px !important;
      flex-wrap: wrap;
    }
    
    .ant-pagination {
      flex-wrap: wrap;
      justify-content: center;
    }
    
    .ant-pagination-options {
      margin-top: 8px;
    }
  }
`;

const PendingUsers: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [detailsRecord, setDetailsRecord] = useState<any>(null);

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchPendingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage]);

  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      const params: any = {
        pageId,
        perPage,
        includeExpired: 'true', // Include expired pending users so admins can resend emails
      };

      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_PENDING_USERS, { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setUsers(data.itemsList || []);
        const paginator = data.paginator || data.pagination;
        setPagination({
          current: paginator?.currentPage || pageId,
          pageSize: paginator?.perPage || perPage,
          total: paginator?.totalCount || paginator?.total || 0,
        });
      } else {
        message.error('Failed to fetch pending users');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch pending users');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (record: any) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_RESEND_VERIFICATION_EMAIL, {
        email: record.email,
        userType: record.userType,
      });

      if (response.data?.data?.wasExpired) {
        message.success('New verification email sent with extended validity');
      } else {
        message.success('Verification email sent successfully');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to resend verification email');
    }
  };

  const handleDelete = async (record: any) => {
    try {
      await apiClient.delete(
        API_ENDPOINTS.ADMIN_DELETE_PENDING_USER.replace('{pendingUserId}', record.id)
      );
      message.success('Pending user deleted successfully');
      fetchPendingUsers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete pending user');
    }
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  // Check if mobile viewport
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const columns = useMemo((): any[] => [
    {
      title: 'Type',
      dataIndex: 'userType',
      key: 'userType',
      width: isMobile ? 100 : undefined,
      render: (type: string) => (
        <Tag color={type === 'Administrator' ? 'blue' : 'green'}>
          {type === 'Administrator' ? <UserOutlined /> : <ShopOutlined />}
          {isMobile ? '' : ` ${type}`}
        </Tag>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: isMobile ? 150 : undefined,
      ellipsis: true,
    },
    {
      title: 'Name',
      key: 'name',
      width: isMobile ? 130 : undefined,
      render: (_: any, record: any) => (
        <Space size="small">
          {record.countryCode && (
            <ReactCountryFlag countryCode={record.countryCode} svg style={{ width: '1.2em', height: '1.2em' }} />
          )}
          <span>{`${record.firstName || ''} ${record.lastName || ''}`.trim() || '-'}</span>
        </Space>
      ),
    },
    ...(users.some((u: any) => u.userType === 'Merchant') ? [{
      title: 'Company',
      key: 'company',
      width: isMobile ? 120 : undefined,
      ellipsis: true,
      render: (_: any, record: any) => {
        if (record.company) {
          return (
            <Space size="small">
              {record.company.logoUrl && (
                <Avatar
                  src={record.company.logoUrl}
                  icon={<ShopOutlined />}
                  size={isMobile ? 20 : 24}
                  style={{ backgroundColor: COLORS.bgSecondary }}
                />
              )}
              <span>{record.company.name}</span>
            </Space>
          );
        }
        return '-';
      },
    }] : []),
    {
      title: 'Reg. Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: isMobile ? 100 : undefined,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isMobile ? 200 : undefined,
      fixed: (isMobile ? 'right' : undefined) as 'right' | undefined,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            onClick={() => setDetailsRecord(record)}
            size="small"
            style={{ fontSize: isMobile ? '10px' : '12px' }}
          >
            {isMobile ? '' : 'View'}
          </Button>
          <Button
            icon={<MailOutlined />}
            onClick={() => handleResendEmail(record)}
            size={isMobile ? 'small' : 'small'}
            style={{ fontSize: isMobile ? '10px' : '12px' }}
          >
            {isMobile ? '' : 'Resend'}
          </Button>
          <Popconfirm
            title={
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>
                  Delete pending user?
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  This permanently deletes this pending registration. This cannot be undone.
                </div>
              </div>
            }
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size={isMobile ? 'small' : 'small'}
              style={{ fontSize: isMobile ? '10px' : '12px' }}
            >
              {isMobile ? '' : 'Delete'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], [isMobile, users]);

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>Pending Users</Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchPendingUsers}
          loading={loading}
        >
          Refresh
        </Button>
      </HeaderContainer>

      <StyledTableCard>
        <TableWrapper>
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            scroll={isMobile ? { x: 800 } : undefined}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: !isMobile,
              showTotal: (total) => (
                <span style={{ 
                  color: COLORS.textSecondary,
                  fontSize: isMobile ? '12px' : '14px'
                }}>
                  Total {total} pending users
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
              size: isMobile ? 'small' : 'default',
              showQuickJumper: !isMobile,
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            size={isMobile ? 'small' : 'middle'}
          />
        </TableWrapper>
      </StyledTableCard>

      <Modal
        title="Pending User Details"
        open={!!detailsRecord}
        onCancel={() => setDetailsRecord(null)}
        footer={
          <Button onClick={() => setDetailsRecord(null)}>Close</Button>
        }
        width={700}
      >
        {detailsRecord && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="User Type">
              <Tag color={detailsRecord.userType === 'Administrator' ? 'blue' : 'green'}>
                {detailsRecord.userType === 'Administrator' ? <UserOutlined /> : <ShopOutlined />} {detailsRecord.userType}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Email">{detailsRecord.email}</Descriptions.Item>
            <Descriptions.Item label="Name">
              {`${detailsRecord.firstName || ''} ${detailsRecord.lastName || ''}`.trim() || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">{detailsRecord.phoneNumber || '-'}</Descriptions.Item>
            <Descriptions.Item label="Country">
              {detailsRecord.countryCode ? (
                <Space size="small">
                  <ReactCountryFlag countryCode={detailsRecord.countryCode} svg style={{ width: '1.2em', height: '1.2em' }} />
                  <span>{detailsRecord.countryCode}</span>
                </Space>
              ) : '-'}
            </Descriptions.Item>
            {detailsRecord.company && (
              <Descriptions.Item label="Company">
                <Space size="small">
                  {detailsRecord.company.logoUrl && (
                    <Avatar
                      src={detailsRecord.company.logoUrl}
                      icon={<ShopOutlined />}
                      size={24}
                      style={{ backgroundColor: COLORS.bgSecondary }}
                    />
                  )}
                  <span>{detailsRecord.company.name}</span>
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Role">{detailsRecord.role || '-'}</Descriptions.Item>
            <Descriptions.Item label="Permissions">
              {detailsRecord.permissions && detailsRecord.permissions.length > 0 ? (
                <Space wrap size="small">
                  {detailsRecord.permissions.map((perm: string, idx: number) => (
                    <Tag key={idx} color="cyan" style={{ margin: 0 }}>{perm}</Tag>
                  ))}
                </Space>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Email Fail">
              <Tag color={detailsRecord.emailFail ? 'red' : 'green'}>
                {detailsRecord.emailFail ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Registration Token">
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {detailsRecord.registrationToken || '-'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Registered At">
              {detailsRecord.createdAt ? new Date(detailsRecord.createdAt).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Expires At">
              {(() => {
                if (!detailsRecord.expiresAt) return '-';
                const expiryDate = new Date(detailsRecord.expiresAt);
                const isExpired = expiryDate < new Date();
                return (
                  <span style={{ color: isExpired ? '#EF4444' : COLORS.textPrimary }}>
                    {expiryDate.toLocaleString()} {isExpired && '(Expired)'}
                  </span>
                );
              })()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </PageContainer>
  );
};

export default PendingUsers;
